import { FieldValue, Timestamp } from "firebase/firestore";
import * as Yup from "yup";

import {
  AdditionalRequirementsPaymentSchema,
  AdditionalRequirementsStep01Schema,
  AdditionalRequirementsStep02Schema,
  BankBaseSchema,
  BankSchema,
  BusinessBaseSchema,
  BusinessSchema,
  ConsentOrdersChildrenLivingArrangementsSchema,
  ConsentOrdersChildrenSchema,
  ConsentOrdersFinancialContributionsSchema,
  ConsentOrdersFinancialResourcesSchema,
  ConsentOrdersFutureNeedsSchema,
  ConsentOrdersNonFinancialSchema,
  ConsentOrdersParentingSchema,
  ConsentOrdersPartnerDetailsSchema,
  ConsentOrdersPartnerIncomeSchema,
  ConsentOrdersRelationshipSchema,
  ConsentOrdersSpecialPaymentSchema,
  ConsentOrdersUserDetailsSchema,
  ConsentOrdersYourIncomeSchema,
  DebtBaseSchema,
  DebtSchema,
  FurnitureAndPersonalEffectsBaseSchema,
  FurnitureAndPersonalEffectsCategorySchema,
  FurnitureAndPersonalEffectsSchema,
  FurnitureAndPersonalEffectsValueSchema,
  InvestmentsBaseSchema,
  InvestmentsSchema,
  OverseasAssetBaseSchema,
  OverseasAssetSchema,
  RealEstateBaseSchema,
  RealEstateDetailsSchema,
  RealEstateMortgageSchema,
  RealEstatePaymentDetailsSchema,
  RealEstateWhatWillHappenSchema,
  SuperannuationBaseSchema,
  SuperannuationDetailsSchema,
  SuperannuationJointSchema,
  SuperannuationSplitSchema,
  VehicleBaseSchema,
  VehicleSchema
} from "../../definitions";

export enum ConsentOrdersPathName {
  Default = "/consent-orders",
  Welcome = "/consent-orders/welcome",
  InitialDetails = "/consent-orders/initial-details",
  RealEstate = "/consent-orders/assets-debts/real-estate",
  Superannuation = "/consent-orders/assets-debts/superannuation",
  Bank = "/consent-orders/assets-debts/bank",
  Vehicle = "/consent-orders/assets-debts/vehicle",
  Investments = "/consent-orders/assets-debts/investments",
  Business = "/consent-orders/assets-debts/business",
  Overseas = "/consent-orders/assets-debts/overseas",
  Furniture = "/consent-orders/assets-debts/furniture",
  Debts = "/consent-orders/assets-debts/debts",
  AdditionalRequirements = "/consent-orders/assets-debts/additional-requirements",
  Summary = "/consent-orders/assets-debts/summary",
  Submit = "/consent-orders/submit"
}

export enum AssetType {
  REAL_ESTATE = "real_estate",
  BANK = "bank",
  BUSINESS = "business",
  VEHICLE = "vehicle",
  SUPERANNUATION = "superannuation",
  SHARES_INVESTMENTS = "shares_investments",
  INVESTMENTS = "investments",
  OVERSEAS = "overseas",
  MISCELLANEOUS = "miscellaneous",
  PERSONAL_AND_HOUSEHOLD = "personal_and_household",
  LIFE_INSURANCE = "life_insurance",
  RETIREMENT_FUND = "retirement_fund",
  TRUST = "trust",
  FURNITURE_AND_PERSONAL = "furniture_and_personal",
  OTHER = "other"
}

export enum DebtType {
  DEBTS = "debts",
  MORTGAGE = "mortgage",
  CREDIT_CARD = "credit_card",
  MISCELLANEOUS_DEBT = "miscellaneous_debt",
  BUSINESS_DEBT = "business_debt",
  HIRE_PURCHASE_LEASE_DEBT = "hire_purchase_lease_debt"
}

// Initial Details
export type ConsentOrdersUserDetailsProps = Yup.InferType<typeof ConsentOrdersUserDetailsSchema>;
export type ConsentOrdersPartnerDetailsProps = Yup.InferType<typeof ConsentOrdersPartnerDetailsSchema>;
export type ConsentOrdersPartnerIncomeProps = Yup.InferType<typeof ConsentOrdersPartnerIncomeSchema>;
export type ConsentOrdersRelationshipProps = Yup.InferType<typeof ConsentOrdersRelationshipSchema>;
export type ConsentOrdersYourIncomeProps = Yup.InferType<typeof ConsentOrdersYourIncomeSchema>;
const _ConsentOrdersFutureNeedsSchema = ConsentOrdersFutureNeedsSchema({ children: {} });
export type ConsentOrdersFutureNeedsProps = Yup.InferType<typeof _ConsentOrdersFutureNeedsSchema>;
export type ConsentOrdersSpecialPaymentProps = Yup.InferType<typeof ConsentOrdersSpecialPaymentSchema>;
export type ConsentOrdersFinancialResourcesProps = Yup.InferType<typeof ConsentOrdersFinancialResourcesSchema>;
export type ConsentOrdersFinancialContributionsProps = Yup.InferType<typeof ConsentOrdersFinancialContributionsSchema>;
export type ConsentOrdersChildrenSchemaProps = Yup.InferType<typeof ConsentOrdersChildrenSchema>;
export type ConsentOrdersChildrenLivingArrangementsProps = Yup.InferType<
  typeof ConsentOrdersChildrenLivingArrangementsSchema
>;
const _ConsentOrdersParentingSchema = ConsentOrdersParentingSchema({ children: {} });
export type ConsentOrdersParentingSchemaProps = Yup.InferType<typeof _ConsentOrdersParentingSchema>;
export type ConsentOrdersNonFinancialSchemaProps = Yup.InferType<typeof ConsentOrdersNonFinancialSchema>;

export type InitialDetails = ConsentOrdersUserDetailsProps &
  ConsentOrdersPartnerDetailsProps &
  ConsentOrdersChildrenSchemaProps &
  ConsentOrdersChildrenLivingArrangementsProps &
  ConsentOrdersPartnerIncomeProps &
  ConsentOrdersRelationshipProps &
  ConsentOrdersYourIncomeProps &
  ConsentOrdersFutureNeedsProps &
  ConsentOrdersSpecialPaymentProps &
  ConsentOrdersFinancialResourcesProps &
  ConsentOrdersFinancialContributionsProps &
  ConsentOrdersParentingSchemaProps &
  ConsentOrdersNonFinancialSchemaProps & {
    isCompleted: boolean;
    currentStep: number;
    createdAt?: Timestamp;
    updatedAt?: Timestamp;
  };

// Assets
export type RealEstateBaseProps = Yup.InferType<typeof RealEstateBaseSchema>;

export type RealEstateDetailsProps = Yup.InferType<typeof RealEstateDetailsSchema>;
export type RealEstateMortgageProps = Yup.InferType<typeof RealEstateMortgageSchema>;
export type RealEstateWhatWillHappenProps = Yup.InferType<typeof RealEstateWhatWillHappenSchema>;
export type RealEstatePaymentDetailsProps = Yup.InferType<typeof RealEstatePaymentDetailsSchema>;

export type RealEstateProps = RealEstateDetailsProps &
  RealEstateMortgageProps &
  RealEstateWhatWillHappenProps & { payments: RealEstatePaymentDetailsProps[] };

//
export type BankBaseProps = Yup.InferType<typeof BankBaseSchema>;
export type BankProps = Yup.InferType<typeof BankSchema>;

//
export type BusinessBaseProps = Yup.InferType<typeof BusinessBaseSchema>;
export type BusinessProps = Yup.InferType<typeof BusinessSchema>;

//
export type FurnitureAndPersonalEffectsBaseProps = Yup.InferType<typeof FurnitureAndPersonalEffectsBaseSchema>;

export type FurnitureAndPersonalEffectsProps = Yup.InferType<typeof FurnitureAndPersonalEffectsSchema>;
export type FurnitureAndPersonalEffectsCategoryProps = Yup.InferType<typeof FurnitureAndPersonalEffectsCategorySchema>;
export type FurnitureAndPersonalEffectsValueProps = Yup.InferType<typeof FurnitureAndPersonalEffectsValueSchema>;

export type FurnitureProps_1 = FurnitureAndPersonalEffectsBaseProps &
  FurnitureAndPersonalEffectsValueProps &
  FurnitureAndPersonalEffectsCategoryProps;

export type FurnitureProps_2 = FurnitureAndPersonalEffectsProps &
  FurnitureAndPersonalEffectsValueProps &
  FurnitureAndPersonalEffectsCategoryProps;

export type FurnitureProps = FurnitureAndPersonalEffectsProps &
  FurnitureAndPersonalEffectsValueProps &
  FurnitureAndPersonalEffectsCategoryProps;

//
export type InvestmentsBaseProps = Yup.InferType<typeof InvestmentsBaseSchema>;
export type InvestmentsProps = Yup.InferType<typeof InvestmentsSchema>;

//
export type OverseasAssetBaseProps = Yup.InferType<typeof OverseasAssetBaseSchema>;
export type OverseasAssetProps = Yup.InferType<typeof OverseasAssetSchema>;

//
export type VehicleBaseProps = Yup.InferType<typeof VehicleBaseSchema>;
export type VehicleProps = Yup.InferType<typeof VehicleSchema>;

//
export type SuperannuationBaseProps = Yup.InferType<typeof SuperannuationBaseSchema>;

export type SuperannuationDetailsProps = Yup.InferType<typeof SuperannuationDetailsSchema>;
export type SuperannuationJointProps = Yup.InferType<typeof SuperannuationJointSchema>;
export type SuperannuationSplitProps = Yup.InferType<typeof SuperannuationSplitSchema>;

export type SuperannuationProps = SuperannuationDetailsProps & SuperannuationJointProps & SuperannuationSplitProps;

//
export type DebtBaseProps = Yup.InferType<typeof DebtBaseSchema>;
export type DebtProps = Yup.InferType<typeof DebtSchema>;

export type AdditionalRequirementsPaymentProps = Yup.InferType<typeof AdditionalRequirementsPaymentSchema>;
export type AdditionalRequirementsStep01Props = Yup.InferType<typeof AdditionalRequirementsStep01Schema>;
export type AdditionalRequirementsStep02Props = Yup.InferType<typeof AdditionalRequirementsStep02Schema>;

export type AdditionalRequirementsProps = AdditionalRequirementsStep01Props & AdditionalRequirementsStep02Props;

type CommonStateProps<T> = {
  type?: T;
  id?: string;
  uid?: string;
  createdAt?: FieldValue | Timestamp;
  updatedAt?: FieldValue | Timestamp;
};

export type RealEstate_InitialState = RealEstateProps & CommonStateProps<AssetType>;

export type Bank_InitialState = BankProps & CommonStateProps<AssetType>;

export type Business_InitialState = BusinessProps & CommonStateProps<AssetType>;

export type Furniture_InitialState = FurnitureProps & CommonStateProps<AssetType>;

export type Furniture_InitialState_1 = FurnitureProps_1;
export type Furniture_InitialState_2 = FurnitureProps_2;

export type Investments_InitialState = InvestmentsProps & CommonStateProps<AssetType>;

export type OverseasAsset_InitialState = OverseasAssetProps & CommonStateProps<AssetType>;

export type Vehicle_InitialState = VehicleProps & CommonStateProps<AssetType>;

export type Superannuation_InitialState = SuperannuationProps & CommonStateProps<AssetType>;

export type Debt_InitialState = DebtProps & CommonStateProps<DebtType>;

export type AdditionalRequirements_InitialState = AdditionalRequirementsProps & CommonStateProps<DebtType>;

export type Asset =
  | RealEstate_InitialState
  | Bank_InitialState
  | Business_InitialState
  | Furniture_InitialState
  | Investments_InitialState
  | OverseasAsset_InitialState
  | Superannuation_InitialState
  | Vehicle_InitialState;

export type Debt = Debt_InitialState;

type AssetDebtData = RealEstateBaseProps &
  BusinessBaseProps &
  BankBaseProps &
  InvestmentsBaseProps &
  OverseasAssetBaseProps &
  VehicleBaseProps &
  SuperannuationBaseProps &
  DebtBaseProps &
  FurnitureProps &
  AdditionalRequirementsProps;

interface AssetDebtSection {
  data: Partial<AssetDebtData>;
  isCompleted: boolean;
}

export interface AssetsDebts {
  real_estate: AssetDebtSection;
  business: AssetDebtSection;
  bank: AssetDebtSection;
  investments: AssetDebtSection;
  overseas: AssetDebtSection;
  furniture: AssetDebtSection & {
    data: FurnitureProps_1;
  };
  vehicle: AssetDebtSection;
  super: AssetDebtSection;
  debts: AssetDebtSection;
  additional_requirements: AssetDebtSection;
  summary: { isCompleted: boolean };
  currentStep: number;
}

export type AssetDebtFields = Omit<AssetsDebts, "currentStep">;

export type ConsentOrdersBase = {
  assetsDebts: AssetsDebts;
  initialDetails: InitialDetails;
  submitted: boolean;
  uid: string;
};
